"use client";

import { memo, useEffect } from "react";
import { useTranslations } from "next-intl";
import { useActionTypeName } from "../../../../hooks/useActionTypeName";
import RiskReportContent from "../../../shared/RiskReportContent";
import { useReportData } from "../../../../hooks/useReportData";
import StateCard from "../../../shared/StateCard";
import type { ChartBlockProps } from "../types";
import type { Contributor } from "@/types/endpoints/reports/GetAiReportById";
import type { CredibilityVerdict } from "@/types/endpoints/assignments/GetAssignmentAttemptDetail";
import { getRiskLevelFromScore } from "../../../../constants/riskConfig";

interface ActionsChartBlockProps extends ChartBlockProps {
  /**
   * Наш credibility_verdict (Laravel). Если передан — данные берём из него,
   * НИКАКОГО запроса к rating-сервису не делаем.
   */
  verdict?: CredibilityVerdict | null;
}

export const ActionsChartBlock = memo(function ActionsChartBlock({
  attemptId,
  onAnalyticsData,
  verdict,
}: ActionsChartBlockProps) {
  const t = useTranslations();
  const getActionTypeName = useActionTypeName('actions');
  // Сначала показываем НАШ credibility_verdict (Laravel), если он сгенерирован
  // (level !== "none" и попытка начата). Пустой contributors при этом — валидный
  // «чистый» результат (нарушений нет): рисуем conclusion, а не заглушку.
  // Если своего вердикта нет — падаем на AI-`actions` из SSE-пайплайна
  // (pipeline.reports.actions), которые приходят после генерации отчёта.
  const useVerdict =
    !!verdict && verdict.level !== "none" && verdict.pattern !== "not_started";
  const { data: report, isLoading } = useReportData<'actions'>(attemptId, 'actions', {
    skip: useVerdict,
  });

  // Текст справа: готовое заключение с бэка, иначе уровень + паттерн вердикта.
  const verdictSummary = useVerdict
    ? verdict!.conclusion?.trim() ||
      `${t(`verdict-level-${verdict!.level}`)} — ${t(`verdict-pattern-${verdict!.pattern}`)}`
    : "";

  // Передаём данные аналитики наверх (правую колонку-«текст» рисует модалка из summary).
  useEffect(() => {
    if (!onAnalyticsData) return;
    if (useVerdict) {
      onAnalyticsData({
        summary_ru: { content: verdictSummary },
        summary_en: { content: verdictSummary },
        summary_kz: { content: verdictSummary },
      } as never);
    } else if (report) {
      const reportData = Array.isArray(report) ? report[0] : report;
      onAnalyticsData(reportData);
    }
  }, [report, onAnalyticsData, useVerdict, verdictSummary]);

  if (useVerdict) {
    const contributors: Contributor[] = verdict!.contributors.map((c) => ({
      action_type: c.type,
      count: c.count,
      points: c.penalty,
      total: 100,
      share: 0,
    }));

    // Названия берём из label_key вердикта (verdict-action-*), как в гайде §5.
    // Фолбэк на enum, если перевода нет.
    const verdictActionName = (type: string): string => {
      const key = `verdict-action-${type}`;
      return t.has(key) ? t(key) : getActionTypeName(type);
    };

    // Нарушений нет — показываем текст-заключение вместо пустого списка.
    if (!contributors.length) {
      return (
        <div className="flex flex-col gap-4 min-h-0 h-full">
          <h3 className="text-sm font-semibold text-[#1A1A1A]">{t("label-actions-report")}</h3>
          <p className="text-sm text-muted-foreground">{verdictSummary}</p>
        </div>
      );
    }

    // Цвет бейджей — по БАЛЛУ доверия вердикта (как круг сзади), а НЕ по verdict.level.
    // verdict.level="high" = высокое ДОВЕРИЕ (хорошо), но в шкале риска "high" = красный.
    // getRiskLevelFromScore переводит балл (95) → low → зелёный, синхронно с кругом.
    const verdictScore =
      typeof verdict!.credibility === "number" ? verdict!.credibility : 0;

    return (
      <RiskReportContent
        risk={{ score: verdictScore, level: getRiskLevelFromScore(verdictScore) }}
        contributors={contributors}
        getActionTypeName={verdictActionName}
        variant="with-title"
        title={t("label-actions-report")}
      />
    );
  }

  if (isLoading) {
    return <StateCard type="loading" />;
  }

  if (!report) {
    return <StateCard type="empty" />;
  }

  const reportData = Array.isArray(report) ? report[0] : report;

  return (
    <RiskReportContent
      risk={reportData.risk}
      contributors={reportData.contributors}
      getActionTypeName={getActionTypeName}
      variant="with-title"
      title={t("label-actions-report")}
    />
  );
});
